import React, { useState, useEffect } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../firebase";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  Avatar,
} from "@mui/material";
import {
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import Navbar from "../components/Navbar";
import FooterSection from "../components/home/FooterSection";
import FlyCodeBanner from "../components/home/FlyCodeBanner";
import StatusBadge, { getStatusConfig } from "../components/StatusBadge";

function Account() {
  const [user, loading] = useAuthState(auth);
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [originalDisplayName, setOriginalDisplayName] = useState("");
  const [saving, setSaving] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [userDocData, setUserDocData] = useState(null); // Full user doc for payment/plan display

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    }
  }, [user, loading, navigate]);

  // Load user data from Firestore
  useEffect(() => {
    if (user) {
      const loadUserData = async () => {
        try {
          const userDocRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserDocData(userData); // Store full doc for payment status display
            setDisplayName(userData.displayName || user.displayName || "");
            setOriginalDisplayName(userData.displayName || user.displayName || "");
          } else {
            setDisplayName(user.displayName || "");
            setOriginalDisplayName(user.displayName || "");
          }

          setEmail(user.email || "");
        } catch (error) {
          console.error("Error loading user data:", error);
        }
      };

      loadUserData();
    }
  }, [user]);

  const handleSaveProfile = async () => {
    if (!user) return;

    setSaving(true);
    try {
      const userDocRef = doc(db, "users", user.uid);
      await updateDoc(userDocRef, {
        displayName: displayName,
      });
      setOriginalDisplayName(displayName);
      alert("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating profile:", error);
      alert("Failed to update profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // TODO: Verify cancelSubscription function parameters from Firebase Functions
  // The function is deployed but we need to confirm:
  // 1. Does it require any parameters or use auth context?
  // 2. What does it return?
  // Check Firebase Console > Functions > cancelSubscription for details
  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const functions = getFunctions();
      const cancelSubscription = httpsCallable(functions, "cancelSubscription");
      await cancelSubscription();
      alert("Subscription cancelled successfully.");
      setCancelDialogOpen(false);
    } catch (error) {
      console.error("Error cancelling subscription:", error);
      alert("Failed to cancel subscription. Please contact support.");
    } finally {
      setCancelling(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#f5f5f5",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Don't render if no user (will redirect)
  if (!user) {
    return null;
  }

  const hasNameChanged = displayName !== originalDisplayName;

  return (
    <>
      <FlyCodeBanner />
      <Navbar />
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#f5f5f5",
          paddingTop: "140px",
          paddingBottom: "60px",
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              marginBottom: "32px",
              textAlign: "center",
              color: "#1a1a1a",
            }}
          >
            Account Hub
          </Typography>

          <Card
            sx={{
              borderRadius: "16px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
            }}
          >
            <CardContent sx={{ padding: "40px" }}>
              {/* Profile Information Section */}
              <Box sx={{ marginBottom: "40px" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    marginBottom: "24px",
                  }}
                >
                  <Avatar
                    src={user.photoURL}
                    alt={displayName}
                    sx={{ width: 64, height: 64 }}
                  />
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, color: "#1a1a1a" }}
                  >
                    Profile Information
                  </Typography>
                  {/* Dynamic status badge reflecting current payment tier */}
                  <StatusBadge paymentStatus={userDocData?.paymentStatus} />
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                  <TextField
                    label="Display Name"
                    variant="outlined"
                    fullWidth
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                      },
                    }}
                  />

                  <TextField
                    label="Email"
                    variant="outlined"
                    fullWidth
                    value={email}
                    disabled
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: "#f9f9f9",
                      },
                    }}
                  />

                  {hasNameChanged && (
                    <Button
                      variant="contained"
                      onClick={handleSaveProfile}
                      disabled={saving}
                      sx={{
                        backgroundColor: "#007CFF",
                        color: "#ffffff",
                        borderRadius: "12px",
                        padding: "12px 24px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "16px",
                        "&:hover": {
                          backgroundColor: "#0066cc",
                        },
                      }}
                    >
                      {saving ? <CircularProgress size={24} color="inherit" /> : "Save Changes"}
                    </Button>
                  )}
                </Box>
              </Box>

              <Divider sx={{ marginY: "32px" }} />

              {/* Dynamic Subscription Section — shows plan-specific info based on paymentStatus */}
              <Box sx={{ marginBottom: "40px" }}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, marginBottom: "16px", color: "#1a1a1a" }}
                >
                  Subscription
                </Typography>

                {(() => {
                  const status = userDocData?.paymentStatus || "free";
                  const config = getStatusConfig(status);

                  if (status === "paid") {
                    return (
                      <>
                        <Typography sx={{ fontSize: "16px", color: "#333", marginBottom: "8px" }}>
                          {config.planName} — {userDocData?.plan === "annual" ? "Annual" : "Monthly"}
                        </Typography>
                        {userDocData?.subscriptionEndDate && (
                          <Typography sx={{ fontSize: "14px", color: "#666", marginBottom: "16px" }}>
                            Renews on {new Date(userDocData.subscriptionEndDate).toLocaleDateString()}
                          </Typography>
                        )}
                        <Button
                          variant="outlined"
                          onClick={() => setCancelDialogOpen(true)}
                          disabled={true}
                          sx={{
                            borderColor: "#d32f2f", color: "#d32f2f", borderRadius: "12px",
                            padding: "12px 24px", textTransform: "none", fontWeight: 600, fontSize: "16px",
                            "&:hover": { borderColor: "#b71c1c", backgroundColor: "rgba(211, 47, 47, 0.04)" },
                            "&.Mui-disabled": { borderColor: "#bdbdbd", color: "#9e9e9e" },
                          }}
                        >
                          Cancel Subscription (Coming Soon)
                        </Button>
                      </>
                    );
                  }

                  if (status === "org") {
                    return (
                      <>
                        <Typography sx={{ fontSize: "16px", color: "#333", marginBottom: "8px" }}>
                          {config.planName} — {userDocData?.activeOrgCode?.organizationName || "Organization"}
                        </Typography>
                        {userDocData?.activeOrgCode?.graduationDate && (
                          <Typography sx={{ fontSize: "14px", color: "#666" }}>
                            Access expires: {new Date(userDocData.activeOrgCode.graduationDate).toLocaleDateString()}
                          </Typography>
                        )}
                      </>
                    );
                  }

                  if (status === "trial") {
                    const expiresAt = userDocData?.activePromoCode?.expiresAt;
                    const daysLeft = expiresAt
                      ? Math.max(0, Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24)))
                      : null;
                    return (
                      <>
                        <Typography sx={{ fontSize: "16px", color: "#333", marginBottom: "8px" }}>
                          {config.planName}
                          {daysLeft !== null && ` — ${daysLeft} day${daysLeft !== 1 ? "s" : ""} remaining`}
                        </Typography>
                        {expiresAt && (
                          <Typography sx={{ fontSize: "14px", color: "#666", marginBottom: "16px" }}>
                            Expires on {new Date(expiresAt).toLocaleDateString()}
                          </Typography>
                        )}
                        <Button
                          variant="contained"
                          href="/pricing"
                          sx={{
                            backgroundColor: "#007CFF", color: "#ffffff", borderRadius: "12px",
                            padding: "12px 24px", textTransform: "none", fontWeight: 600, fontSize: "16px",
                            "&:hover": { backgroundColor: "#0066cc" },
                          }}
                        >
                          Upgrade to Pro
                        </Button>
                      </>
                    );
                  }

                  /* Free plan */
                  return (
                    <>
                      <Typography sx={{ fontSize: "16px", color: "#333", marginBottom: "16px" }}>
                        {config.planName} — Basic access
                      </Typography>
                      <Button
                        variant="contained"
                        href="/pricing"
                        sx={{
                          backgroundColor: "#007CFF", color: "#ffffff", borderRadius: "12px",
                          padding: "12px 24px", textTransform: "none", fontWeight: 600, fontSize: "16px",
                          "&:hover": { backgroundColor: "#0066cc" },
                        }}
                      >
                        Upgrade to Pro
                      </Button>
                    </>
                  );
                })()}
              </Box>

              <Divider sx={{ marginY: "32px" }} />

              {/* Account Actions Section */}
              <Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, marginBottom: "16px", color: "#1a1a1a" }}
                >
                  Account Actions
                </Typography>

                <Button
                  variant="outlined"
                  onClick={handleSignOut}
                  sx={{
                    borderColor: "#757575",
                    color: "#424242",
                    borderRadius: "12px",
                    padding: "12px 24px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "16px",
                    "&:hover": {
                      borderColor: "#424242",
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  Sign Out
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>

      <FooterSection />

      {/* Cancel Subscription Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            padding: "8px",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: "20px" }}>
          Cancel Subscription
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing period.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button
            onClick={() => setCancelDialogOpen(false)}
            sx={{
              color: "#757575",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Keep Subscription
          </Button>
          <Button
            onClick={handleCancelSubscription}
            disabled={cancelling}
            sx={{
              backgroundColor: "#d32f2f",
              color: "#ffffff",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#b71c1c",
              },
            }}
          >
            {cancelling ? <CircularProgress size={24} color="inherit" /> : "Cancel Subscription"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Account;
